import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  Injector,
  PLATFORM_ID,
  Renderer2,
} from '@angular/core';
import { RESOLVED_TENANT } from '@core/tokens/resolved-tenant.token';
import { DOCUMENT, isPlatformBrowser, NgComponentOutlet } from '@angular/common';
import { Meta } from '@angular/platform-browser';
import { NavigationStart, Router, RouterOutlet } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter } from 'rxjs';
import { MatIconModule } from '@angular/material/icon';
import { MatSidenavModule } from '@angular/material/sidenav';
import { createBlockScrollStrategy } from '@angular/cdk/overlay';
import { AuthenticationService } from '@core/authentication/authentication.service';
import { AppHeaderComponent } from '@shared/components/header/header.component';
import { AppFooterComponent } from '@shared/components/footer/footer.component';
import { DrawerNavComponent } from '@shared/components/drawer-nav/drawer-nav.component';
import { DrawerBasketComponent, FloatingBasketButtonComponent } from '@features/basket';
import { SiteStore } from '@core/site/site.store';
import { SkipLinkComponent } from '@abook-ui/components/skip-link/skip-link.component';
import { DEV_TOOLBAR } from '../environments/dev-tools';

@Component({
  selector: 'app-root',
  imports: [
    SkipLinkComponent,
    RouterOutlet,
    MatIconModule,
    MatSidenavModule,
    AppHeaderComponent,
    AppFooterComponent,
    NgComponentOutlet,
    DrawerNavComponent,
    DrawerBasketComponent,
    FloatingBasketButtonComponent,
  ],
  template: `
    <!-- Not fullscreen: the document scrolls (#711, see the customer-app spec). -->
    <mat-sidenav-container class="app-shell" [class.app-shell--dev]="showDevBar()">
      @if (showChrome()) {
        <mat-sidenav
          position="start"
          mode="over"
          fixedInViewport
          style="--mat-sidenav-container-width: min(320px, 100vw)"
          [opened]="site.leftDrawerOpen()"
          (closedStart)="site.closeLeftDrawer()"
        >
          <app-drawer-nav />
        </mat-sidenav>
      }
      @if (showBasket()) {
        <mat-sidenav
          position="end"
          mode="over"
          fixedInViewport
          style="--mat-sidenav-container-width: min(400px, 100vw)"
          [opened]="site.rightDrawerOpen()"
          (closedStart)="site.closeRightDrawer()"
        >
          <app-drawer-basket />
        </mat-sidenav>
      }

      <mat-sidenav-content>
        <!-- First focusable element, with or without the header (the maintenance page has none).
             The drawers before it are closed, so Tab cannot reach into them (#689). -->
        <abook-skip-link />
        @if (showMaintenanceBanner()) {
          <div class="maintenance-banner" role="status">
            <mat-icon aria-hidden="true">warning</mat-icon>
            <span>Sitet er i vedligeholdelsestilstand og er ikke tilgængeligt for besøgende.</span>
          </div>
        }
        @if (showDevBar() && showChrome()) {
          <ng-container *ngComponentOutlet="devToolbar" />
        }
        @if (showChrome()) {
          <app-header />
        }
        <!-- Not <main>: each page renders its own through abook-page (#686). -->
        <div class="app-shell__page">
          <router-outlet />
        </div>
        @if (showChrome()) {
          <app-footer />
        }
        @if (showBasket()) {
          <app-floating-basket-button />
        }
      </mat-sidenav-content>
    </mat-sidenav-container>
  `,
  styles: [
    `
      /* Undo Material's inner scroll containers, so the document scrolls (#711). */
      .app-shell {
        overflow: visible;
        --top-chrome-height: var(--space-80);
      }
      .app-shell--dev {
        --top-chrome-height: calc(var(--space-80) + 48px);
      }
      mat-sidenav-content {
        display: flex;
        flex-direction: column;
        min-height: 100dvh;
        overflow: visible;
      }
      /* Keeps the footer below the fold while a route loads (CLS, #1033). svh: fixed, no relayout when the URL bar toggles. */
      .app-shell__page {
        flex: 1;
        min-height: calc(100svh - var(--top-chrome-height));
      }
      .maintenance-banner {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        padding: 8px 16px;
        background: var(--mat-sys-error-container);
        color: var(--mat-sys-on-error-container);
        font-size: 14px;
        font-weight: 600;
        text-align: center;
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class App {
  private auth = inject(AuthenticationService);
  protected site = inject(SiteStore);
  protected isLocalhost = inject(DOCUMENT).location.hostname === 'localhost';
  private readonly isDevDomain = inject(DOCUMENT).location.hostname === 'customer-app.aktivby.dk';
  /**
   * localStorage (the dev-env override) doesn't exist during SSR/SSG prerendering, so any
   * prerendered page would otherwise bake in a fixed "demo" badge that then visibly flips to
   * the real value once the client hydrates. Restricting the dev bar to the browser means it
   * mounts once, already showing the correct value, instead of flashing wrong-then-right.
   */
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  // Null in production: the toolbar is not in that bundle at all (#906).
  protected readonly devToolbar = DEV_TOOLBAR;
  protected showDevBar = computed(
    () =>
      this.isBrowser &&
      !!this.devToolbar &&
      (this.isLocalhost || this.isDevDomain || !!this.site.tenantConfig()?.core?.showDevBar),
  );
  protected showChrome = computed(() => !this.site.isMaintenanceModePage());
  // The basket drawer and the floating button are the booking module's only entry points
  // outside a route, so they are gated here rather than by bookingModuleGuard (#586).
  protected showBasket = computed(() => this.showChrome() && this.site.showBookingModule());
  protected showMaintenanceBanner = computed(
    () => !!this.site.settings()?.maintenanceModeEnabled && this.auth.isSuperAdmin() && this.showChrome(),
  );

  constructor() {
    const renderer = inject(Renderer2);
    const doc = inject(DOCUMENT);
    const meta = inject(Meta);

    // Lock the page behind an open drawer, as MatDialog does (#711). A navigation closes both
    // drawers: a lock left on through it would swallow the router's scroll to the top and then
    // restore the previous page's position (back/forward, a dialog link over the basket).
    if (this.isBrowser) {
      inject(Router)
        .events.pipe(
          filter((e) => e instanceof NavigationStart),
          takeUntilDestroyed(),
        )
        .subscribe(() => {
          this.site.closeLeftDrawer();
          this.site.closeRightDrawer();
        });

      const scrollLock = createBlockScrollStrategy(inject(Injector));
      effect(() =>
        this.site.leftDrawerOpen() || this.site.rightDrawerOpen()
          ? scrollLock.enable()
          : scrollLock.disable(),
      );
    }

    // Which tenant this page was rendered for, so the A2 quality check (#1008) can tell a page
    // rendered for the wrong host from the right one. The registry's name; set once.
    renderer.setAttribute(doc.documentElement, 'data-tenant', inject(RESOLVED_TENANT).tenantName);

    // tenantConfig is resolved once, synchronously (RESOLVED_TENANT), and never
    // changes afterwards — a separate effect means this runs exactly once,
    // instead of re-running (and re-writing the same favicon) on every
    // unrelated settings()/theme() change in the effect below.
    effect(() => {
      const faviconPath = this.site.tenantConfig()?.core.favicon;
      if (faviconPath) {
        const faviconEl = doc.querySelector<HTMLLinkElement>('link[rel="icon"]');
        if (faviconEl) renderer.setAttribute(faviconEl, 'href', faviconPath);
      }
    });

    effect(() => {
      const theme = this.site.theme();
      renderer.setAttribute(doc.documentElement, 'data-theme', theme);

      const s = this.site.settings();
      if (!s) return;

      const siteName = s.name ?? 'Abook';
      const image = s.heroImage ?? s.logo;

      if (this.isBrowser) {
        const themeColor = getComputedStyle(doc.documentElement)
          .getPropertyValue('--action-primary')
          .trim();
        if (themeColor) meta.updateTag({ name: 'theme-color', content: themeColor });
      }

      meta.updateTag({ property: 'og:site_name', content: siteName });
      meta.updateTag({ property: 'og:type', content: 'website' });
      if (image) meta.updateTag({ property: 'og:image', content: image });
    });
  }
}
